import { Loader2, Pencil, Search } from 'lucide-react';
import PowerDetailHero from '@/components/help-center/powers/PowerDetailHero';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { PowerInfo } from '@/lib/powers-data';
import { cn } from '@/lib/utils';

const PLACEHOLDER_POWER: PowerInfo = {
  id: 0,
  name: 'Nome do power',
  slug: 'power',
  description: {
    short: 'Digite o nome do power para carregar os dados da API xatBlog.',
    long: '',
  },
  prices: {
    store: '0',
    fairtrade_min: 0,
    fairtrade_max: 0,
  },
  status: false,
  released: new Date().toISOString().slice(0, 10),
  category: 'smiley',
  type: 'smiley',
  limited: false,
  smilies: [],
  pawns: [],
};

type PowerEditorHeroPreviewProps = {
  mode: 'new' | 'edit';
  power: PowerInfo | null;
  nameValue: string;
  onNameChange: (name: string) => void;
  /** Confirma o nome e dispara o carregamento da API. */
  onConfirmName: () => void;
  apiLoading: boolean;
  apiMessage: string | null;
  slugConflict: boolean;
  showEditButton: boolean;
  onEditClick: () => void;
};

export function PowerEditorHeroPreview({
  mode,
  power,
  nameValue,
  onNameChange,
  onConfirmName,
  apiLoading,
  apiMessage,
  slugConflict,
  showEditButton,
  onEditClick,
}: PowerEditorHeroPreviewProps) {
  const showNameGate = mode === 'new' && !showEditButton;
  const heroPower = power ?? PLACEHOLDER_POWER;
  const canConfirm =
    nameValue.trim().length >= 2 && !apiLoading && !slugConflict;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_60px_-28px_rgba(0,0,0,0.65)] ring-1 ring-sky-500/20">
      {showEditButton ? (
        <div className="absolute right-4 top-4 z-40 sm:right-5 sm:top-5">
          <Button
            type="button"
            onClick={onEditClick}
            className={cn(
              'h-10 rounded-xl border border-white/15 bg-[#030023]/75 px-4 font-poppins text-sm font-semibold text-white',
              'shadow-lg backdrop-blur-md hover:bg-sky-500/20 hover:border-sky-400/40'
            )}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Editar informações
          </Button>
        </div>
      ) : null}

      <div className={cn(showNameGate && 'pointer-events-none select-none')}>
        <PowerDetailHero power={heroPower} compact disableExternalLinks />
      </div>

      {showNameGate ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#030023]/55 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md space-y-3 rounded-2xl border border-white/15 bg-[#030023]/90 p-5 shadow-2xl shadow-black/40 ring-1 ring-sky-500/20 sm:p-6">
            <div className="text-center">
              <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/60">
                Novo power
              </p>
              <h3 className="mt-1 font-poppins text-lg font-bold text-white">Nome do power</h3>
              <p className="mt-1 font-inter text-xs text-slate-400">
                Digite o nome e confirme para carregar os dados da API. Nada é preenchido antes da confirmação.
              </p>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={nameValue}
                onChange={(e) => onNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canConfirm) onConfirmName();
                  }
                }}
                placeholder="Ex.: Astrox, Maus, Bowgal…"
                autoFocus
                disabled={apiLoading}
                className={cn(
                  'h-12 rounded-xl border-white/15 bg-white/[0.06] pl-10 font-poppins text-base text-white',
                  'placeholder:text-slate-500 focus-visible:ring-sky-400/40'
                )}
              />
              {apiLoading ? (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-sky-300" />
              ) : null}
            </div>
            {slugConflict ? (
              <p className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 font-inter text-xs text-rose-200">
                Já existe um power com este slug no catálogo.
              </p>
            ) : null}
            {apiMessage && !apiLoading ? (
              <p
                className={cn(
                  'rounded-xl border px-3 py-2 font-inter text-xs',
                  apiMessage.includes('importados')
                    ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'
                    : 'border-white/10 bg-white/[0.04] text-slate-300'
                )}
              >
                {apiMessage}
              </p>
            ) : null}
            <Button
              type="button"
              onClick={onConfirmName}
              disabled={!canConfirm}
              className={cn(
                'h-11 w-full rounded-xl font-poppins text-sm font-semibold',
                'bg-sky-500 text-white hover:bg-sky-400',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {apiLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  A carregar…
                </>
              ) : nameValue.trim() ? (
                <>Criar “{nameValue.trim()}”</>
              ) : (
                'Criar power'
              )}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { PLACEHOLDER_POWER };
